import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ChevronRight from '../../icons/ChevronRight';
import './Blog.css';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import { useNavigate } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';
import ExpandableCard from '../../components/ExpandableCard';
import NotFound from '../NotFound';

function BlogPage() {
    const { slug } = useParams();
    const { pageNum } = useParams();
    const { t } = useTranslation();
    const langPrefix = useLanguagePrefix();
    const navigate = useNavigate();
    const [apiCallsDone, setApicallsDone] = useState(false);
    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [trendingArticles, setTrendingArticles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(pageNum ? parseInt(pageNum, 10) : 1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);
     const apiUrl = "https://api.prostay.com";
   // const apiUrl = "https://localhost:7167";
    const [isInitialLoad, setIsInitialLoad] = useState(false);

    const cleanLangPrefix = (langPrefix || '').replace(/\//g, '') || 'en';
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [faqSchema, setFaqSchema] = useState(null);

    const getLocalizedFaqs = (faqs) => {
        const cleanLang = cleanLangPrefix;

        return faqs
            .map(faq => {
                const question = cleanLang === 'en'
                    ? faq.question
                    : faq.questionTranslations?.[cleanLang];
                const answer = cleanLang === 'en'
                    ? faq.answer
                    : faq.answerTranslations?.[cleanLang];

                if (!question || !answer) return null;

                return { question, answer };
            })
            .filter(faq => faq !== null);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!isInitialLoad) {
                setLoading(true);
            }

            const saved = localStorage.getItem('blogSearchTerm') ?? '';
            if (saved) {
                setSearchInput(saved);
                setSearchTerm(saved);
            }
            localStorage.removeItem('blogSearchTerm');

            setCurrentPage(pageNum ? parseInt(pageNum, 10) : 1);
            console.log("setCurrentPage: ", parseInt(pageNum, 10) || 1);
            try {
                await fetchPaginatedArticles(saved || '', pageSize, pageNum || 1, selectedCategory?.id)
            } catch (err) {
                console.error('Error fetching blog data:', err);
            } finally {
                if (!isInitialLoad) {
                    setLoading(false);
                }
            }
        };

        if (!isInitialLoad) {
            fetchData();
        }
    }, [pageNum]);

    useEffect(() => {
        var schema = selectedArticle?.faqs?.length
            ? {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": selectedArticle.faqs
                    .filter(faq => {
                        const question = cleanLangPrefix === 'en' ? faq.question : faq.questionTranslations?.[cleanLangPrefix];
                        const answer = cleanLangPrefix === 'en' ? faq.answer : faq.answerTranslations?.[cleanLangPrefix];
                        return question && answer;
                    })
                    .map(faq => {
                        const question = cleanLangPrefix === 'en' ? faq.question : faq.questionTranslations?.[cleanLangPrefix];
                        const answer = cleanLangPrefix === 'en' ? faq.answer : faq.answerTranslations?.[cleanLangPrefix];

                        return {
                            "@type": "Question",
                            "name": question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": answer
                            }
                        };
                    })
            }
            : null;
        setFaqSchema(schema);
    }, [selectedArticle]);

    const getArticleContent = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.content;
        }

        return article.contentTranslations?.[cleanLangPrefix] || article.content;
    }

    const getArticleTitle = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.title;
        }
        console.log("langPrefix: ", cleanLangPrefix)
        return article.titleTranslations?.[cleanLangPrefix] || article.title;
    }

    const getArticleMetaTitle = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.metaTitle;
        }

        return article.metaTitleTranslations?.[cleanLangPrefix] || article.metaTitle;
    }

    const getArticleMetaDescription = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.metaDescription;
        }

        return article.metaDescriptionTranslations?.[cleanLangPrefix] || article.metaDescription;
    }

    const formattedCategoryName = !selectedArticle && slug
        ? slug.replace(/-/g, ' ')
        : '';

    const selectedCategory = formattedCategoryName
        ? categories.find((c) => c.name.toLowerCase() === formattedCategoryName)
        : null;

    const generateCategorySlug = (categoryName) =>
        categoryName.toLowerCase().replace(/\s+/g, '-');

    const fetchPaginatedArticles = async (_searchTerm, _pageSize, _currentPage, _categoryId) => {
        if (!isInitialLoad) {
            setLoading(true);
        }
        try {
            const requestPayload = {
                searchTerm: _searchTerm,
                pageSize: _pageSize,
                currentPage: _currentPage,
                categoryId: _categoryId
            };

            const res = await fetch(apiUrl + '/WebsiteKnowledgebase/get-blog-articles-paginated', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestPayload)
            });

            if (!res.ok) throw new Error('Failed to fetch paginated articles');

            const responseData = await res.json();

            setArticles(responseData.data);
            console.log("total pages: ", responseData.totalPages)
            setTotalPages(responseData.totalPages);
        } catch (err) {
            console.error('Error loading blog articles:', err);
        } finally {
            if (!isInitialLoad) {
                setLoading(false);
            }
        }
    };

    const fetchTrendingArticles = async () => {
        try {
            const resTrending = await fetch(apiUrl + '/WebsiteKnowledgebase/get-trending-blog-articles');
            if (!resTrending.ok) throw new Error('Failed to fetch trending articles');
            const dataTrending = await resTrending.json();
            setTrendingArticles(dataTrending);
        } catch (err) {
            console.error('Error loading trending articles:', err);
        }
    };

    const fetchCategories = async () => {
        try {

            const resTrending = await fetch(apiUrl + '/WebsiteKnowledgebase/get-blog-articles-categories');
            if (!resTrending.ok) throw new Error('Failed to fetch trending articles');
            const categories = await resTrending.json();
            setCategories(categories);
            return categories;
        } catch (err) {
            console.error('Error loading trending articles:', err);
        }
    };

    const getPreview = (htmlContent) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        let plainText = tempDiv.textContent || tempDiv.innerText || '';
        plainText = plainText.replace(/\[Fig_\d+\]/g, '');
        return plainText.length > 130 ? plainText.substring(0, 130).trim() + '...' : plainText.trim();
    };

    const canonicalUrl = `https://www.prostay.com${langPrefix}/blog${slug ? `/${slug}/` : '/'}`;

    const articleSchema = useMemo(() => {
        if (!selectedArticle) return null;

        const dateOnly = (d) => new Date(d).toISOString().split('T')[0];

        const headline = getArticleTitle(selectedArticle);
        const description =
            getArticleMetaDescription(selectedArticle) ||
            getPreview(getArticleContent(selectedArticle));
        const imageUrl = selectedArticle.headerImageUrl;
        const publishedDate = dateOnly(selectedArticle.addedAt);
        const modifiedDate =
            selectedArticle.updatedAt ? dateOnly(selectedArticle.updatedAt)
                : publishedDate;

        return {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": getArticleTitle(selectedArticle),
            "description": getArticleMetaDescription(selectedArticle),
            "image": imageUrl,
            "author": selectedArticle.authorName && selectedArticle.authorImageUrl
                ? {
                    "@type": "Person",
                    "name": selectedArticle.authorName,
                    "image": selectedArticle.authorImageUrl,
                    "url": `https://prostay.com/author/${selectedArticle.authorName
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`
                }
                : undefined,
            "publisher": {
                "@type": "Organization",
                "name": "Prostay",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://www.prostay.com/images/prostay-logo-dark.svg"
                }
            },
            "datePublished": publishedDate,
            "dateModified": modifiedDate,
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": canonicalUrl
            }
        };
    }, [selectedArticle, canonicalUrl, langPrefix]);


    const pageTitle = selectedArticle
        ? getArticleMetaTitle(selectedArticle) || getArticleTitle(selectedArticle)
        : selectedCategory
            ? `${selectedCategory.name} - Page ${pageNum || '1'} - Prostay`
            : 'Prostay – Hotel Technology News & Tips Blog';

    const pageDescription = selectedArticle
        ? getArticleMetaDescription(selectedArticle) || getPreview(getArticleContent(selectedArticle))
        : selectedCategory
            ? selectedCategory.metaDescription || ''
            : 'Read hotel tech trends, expert tips, and news from the Prostay team. Learn how to optimize your operations and guest experience.';

    function formatDate(date) {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }

    const handleCategoryClick = (catName) => {
        const slugified = catName.toLowerCase().replace(/\s+/g, '-');
        navigate(`${langPrefix}/blog/${slugified}/`);
    };


    useEffect(() => {
        if (!selectedArticle || !apiCallsDone) return;

        const postContent = document.getElementById("post-content");
        const tocContainer = document.getElementById("toc");

        if (!postContent || !tocContainer) return;

        const headings = postContent.querySelectorAll("h2");
        if (headings.length === 0) return;

        const toc = document.createElement("ol");
        toc.className = "toc-list";

        headings.forEach((heading) => {
            const text = heading.textContent?.trim();
            if (!text) return;

            const level = parseInt(heading.tagName.substring(1));
            const id = heading.id || text.toLowerCase().replace(/[^\w]+/g, "-");

            if (!heading.id) heading.id = id;

            const li = document.createElement("li");
            li.className = `toc-item level-${level}`;

            const a = document.createElement("a");
            a.href = `#${id}`;
            a.textContent = text;

            a.addEventListener("click", (e) => {
                e.preventDefault();
                const target = document.getElementById(heading.id);
                if (target) {
                    const offset = 100;
                    const bodyRect = document.body.getBoundingClientRect().top;
                    const elementRect = target.getBoundingClientRect().top;
                    const elementPosition = elementRect - bodyRect;
                    const offsetPosition = elementPosition - offset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth",
                    });
                }
            });

            li.appendChild(a);
            toc.appendChild(li);
        });


        tocContainer.innerHTML = "";
        tocContainer.appendChild(toc);
    }, [selectedArticle, apiCallsDone]);


    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const initializePage = async () => {
            try {
                setLoading(true);
                setApicallsDone(false);
                setIsInitialLoad(true);

                const resArticle = await fetch(apiUrl + '/WebsiteKnowledgebase/get-article-by-slug/' + slug);

                if (resArticle.status === 200) {
                    const currentArticle = await resArticle.json();
                    if (currentArticle) {
                        setSelectedArticle(currentArticle);
                    }
                } else if (resArticle.status === 204) {
                    const resCat = await fetch(apiUrl + '/WebsiteKnowledgebase/get-category-by-slug/' + slug);

                    if (resCat.status === 200) {
                        const currentCategory = await resCat.json();
                        if (currentCategory) {
                            await fetchPaginatedArticles('', pageSize, pageNum || 1, currentCategory.id);
                        } else {
                            setNotFound(true);
                            return;
                        }
                    } else if (resCat.status === 204) {
                        setNotFound(true);
                        return;
                    } else {
                        await fetchPaginatedArticles('', pageSize, pageNum || 1, null);
                    }
                } else {
                    console.error('Failed to fetch article, status:', resArticle.status);
                }

                await fetchTrendingArticles();
                await fetchCategories();
            } catch (error) {
                console.error('Error during blog page initialization:', error);
            } finally {
                setLoading(false);
                setApicallsDone(true);
                setIsInitialLoad(false);
            }
        };

        initializePage();
    }, [slug]);

    const NotFoundWithStatus = () => {
        useEffect(() => {
            const statusMeta = document.createElement('meta');
            statusMeta.name = 'http-status';
            statusMeta.content = '404';
            document.head.appendChild(statusMeta);

            const robotsMeta = document.createElement('meta');
            robotsMeta.name = 'robots';
            robotsMeta.content = 'noindex';
            document.head.appendChild(robotsMeta);

            return () => {
                document.head.removeChild(statusMeta);
                document.head.removeChild(robotsMeta);
            };
        }, []);

        return <NotFound />;
    };


    if (notFound) return <NotFoundWithStatus />;

    const handlePerformSearch = async (term) => {
        setSearchTerm(term.trim());

        if (pageNum !== '1') {
            if (term.trim()) {
                localStorage.setItem('blogSearchTerm', term.trim());
            } else {
                localStorage.removeItem('blogSearchTerm');
            }
            navigate(`${langPrefix}/blog/${slug}/page/1/`);
        } else {
            setLoading(true);
            await fetchPaginatedArticles(term.trim(), 10, 1, selectedCategory?.id)
            setLoading(false);
        }
    };

    const handlePageChange = async (event, newPage) => {
        navigate(`${langPrefix}/blog/${slug}/page/${newPage}/`);
    };

    const formatShortMonthDayYear = (dateInput) => {
        const date = new Date(dateInput);

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    }



    return (
        <div className="blog">
            <Helmet>
                {faqSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify(faqSchema)}
                    </script>
                )}
                {articleSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify(articleSchema)}
                    </script>
                )}
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <link rel="alternate" href={"https://www.prostay.com/en" + `/blog${slug ? `/${slug}/` : '/'}`} />
                <link rel="alternate" href={"https://www.prostay.com/es" + `/blog${slug ? `/${slug}/` : '/'}`} />

                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />

            </Helmet>

            <div className="background-image">
                <div className="background-image-start">
                    <div className="d-flex align-items-center gap-2 gap-md-3 breadcrumbs">
                        <a href={`${langPrefix}/`} style={{ textDecoration: 'none' }}><small>{t('Home')}</small></a>
                        <ChevronRight />
                        <a href={`${langPrefix}/blog/`} style={{ textDecoration: 'none' }}>
                            <small className={!selectedArticle && !selectedCategory ? 'text-primary' : ''}>{t('Blog')}</small>
                        </a>
                        {(selectedArticle || selectedCategory) && (
                            <>
                                <ChevronRight />
                                <small className="text-primary">{selectedArticle ? selectedArticle.title : selectedCategory?.name}</small>
                            </>
                        )}
                    </div>
                    <div className="mt-4 d-flex flex-column">
                        <h1>{selectedArticle ? getArticleTitle(selectedArticle) : selectedCategory?.name || t('Prostay Blog')}</h1>
                        {
                            selectedArticle &&
                            <>
                                {
                                    selectedArticle?.addedAt &&
                                    <div className='mt-2' style={{ width: 'fit-content', backgroundColor: '#F8F9FC', border: '1px solid #D5D9EB', borderRadius: '100px', paddingInline: 5, height: 21, color: '#363F72', fontSize: 12, fontFamily: 'Inter' }}>
                                        {formatShortMonthDayYear(selectedArticle?.addedAt)}
                                    </div>
                                }
                                {
                                    selectedArticle?.authorName && selectedArticle?.authorImageUrl &&
                                    <div className='d-flex mt-3 gap-2 align-items-center'>
                                        <img style={{ height: 40, width: 40, borderRadius: '100px', objectFit: 'cover' }} src={selectedArticle?.authorImageUrl} />
                                        <span style={{ color: '#344054', fontFamily: 'Inter', fontSize: 14, fontWeight: 500 }}>{selectedArticle?.authorName}</span>
                                    </div>
                                }
                            </>
                        }
                    </div>
                </div>
            </div>

            {(loading || isInitialLoad) ? (
                <div className="d-flex align-items-center justify-content-center mt-5">
                    <div className="spinner-container">
                        <div className="custom-spinner"></div>
                    </div>
                </div>
            ) : (
                <div className="d-flex flex-column align-items-center justify-content-center">
                    <div className='d-flex mt-4 mb-1 justify-content-end page-content'>
                        <div className='search-container'>
                            <input
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handlePerformSearch(searchInput);
                                    }
                                }}
                                placeholder='Search'
                            />

                            <div onClick={() => {
                                handlePerformSearch(searchInput);
                            }} style={{ cursor: 'pointer' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 21L16.7 16.7M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="#7784A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div className="content-grid page-content mt-2 mt-md-3">
                        <div className='col'>
                            <div className="white-content-card" style={{ height: 'fit-content' }}>
                                <div className="white-content-card p-0 d-none d-md-flex flex-column">
                                    <h5 className='m-0' style={{ fontFamily: 'Inter' }}>{t('Popular Categories')}</h5>
                                    <hr />
                                    {categories.map((cat) => {
                                        const slug = cat.name.toLowerCase().replace(/\s+/g, "-");
                                        const isActive = cat.name.toLowerCase() === formattedCategoryName;

                                        return (
                                            <a
                                                key={cat.name}
                                                href={`${langPrefix}/blog/${slug}/`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setSelectedArticle(null);
                                                    navigate(`${langPrefix}/blog/${slug}/`);
                                                }}
                                                className={`subcategory-link w-100 ${isActive ? "active" : ""}`}
                                                style={{ textDecoration: "none" }}
                                            >
                                                {cat.name}
                                            </a>
                                        );
                                    })}
                                </div>
                                <div className='d-block d-md-none'>
                                    <h5 className='m-0' style={{ fontFamily: 'Inter' }}>{t('Popular Categories')}</h5>
                                    <hr />
                                    <div className='select-container w-100' style={{ position: 'relative' }}>
                                        <select
                                            className="form-select w-100"
                                            value={formattedCategoryName}
                                            onChange={(e) => handleCategoryClick(e.target.value)}
                                        >
                                            <option value="">{t('Select a category')}</option>
                                            {categories.map((cat) => (
                                                <option key={cat.name} value={cat.name}>
                                                    {cat.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="white-content-card mt-3" style={{ height: 'fit-content' }}>
                                <h5 className='m-0' style={{ fontFamily: 'Inter' }}>{t('Trending Post')}</h5>
                                <hr />
                                {
                                    trendingArticles?.map((article, index) =>
                                        <a className='d-flex gap-2 trending-article-card' style={{ textDecoration: 'none' }} href={`${langPrefix}/blog/${article.slug}/`}>
                                            <img src={article.headerImageUrl} />
                                            <div className='col'>
                                                <label>{formatDate(article.addedAt)}</label>
                                                <h3>{getArticleTitle(article)}</h3>
                                            </div>
                                        </a>
                                    )
                                }
                            </div>
                        </div>



                        <div className="white-content-card articles-card">

                            {selectedArticle ? (
                                <>
                                    <h5>{t('Table of contents')}</h5>
                                    <nav id="toc" />
                                    <div id="post-content">
                                        <div className="article-content" dangerouslySetInnerHTML={{ __html: getArticleContent(selectedArticle) }} />
                                    </div>
                                    {selectedArticle?.faqs?.length > 0 && (
                                        <div className="faq-section mt-5">
                                            <h5>{t('Frequently Asked Questions')}</h5>
                                            <div className="faq-list">
                                                {getLocalizedFaqs(selectedArticle.faqs).map((faq, index) => (
                                                    <ExpandableCard content={faq.answer} title={faq.question} />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                </>
                            ) : (
                                <>
                                    {
                                        articles && articles.length > 0 ?
                                            <>
                                                <div className="articles-grid">
                                                    {articles.map(article => (
                                                        <a
                                                            style={{ textDecoration: 'none' }}
                                                            key={article.id}
                                                            className="d-flex flex-column article"
                                                            href={`${langPrefix}/blog/${article.slug}/`}>
                                                            <img src={article.headerImageUrl} />
                                                            <label className='mt-2'>{formatDate(article.addedAt)}</label>
                                                            <h3 className='mt-2 mb-1'>{getArticleTitle(article)}</h3>
                                                            <small>{getPreview(getArticleContent(article))}</small>
                                                        </a>
                                                    ))}
                                                </div>
                                                {
                                                    totalPages > 1 &&
                                                    <div className="d-flex justify-content-center mt-4">
                                                        <Pagination
                                                            count={totalPages}
                                                            page={currentPage}
                                                            onChange={handlePageChange}
                                                            color="primary"
                                                        />
                                                    </div>
                                                }
                                            </>
                                            :
                                            <div className='d-flex justify-content-center'>
                                                <small>No blog posts found</small>
                                            </div>
                                    }
                                </>
                            )}
                        </div>
                    </div>
                    {
                        selectedCategory &&
                        <div className='d-flex mt-4 mb-1 justify-content-center '>
                            <div className='white-content-card page-content'>
                                <p className='m-0'>{selectedCategory?.description}</p>
                            </div>
                        </div>
                    }
                </div>
            )}
        </div>
    );
}
export default BlogPage;
